import React, { createContext, useContext, useEffect, useReducer, useState } from 'react'

import validationReducer from './validationReducer'
import { SET_MY_VALIDATIONS, SET_VALIDATIONS } from '../types'
import { UserContext } from '../user/userState'
import { add_document_event, create_validation, fetch_validations, resolve_validation } from '../../services/firestore'
import { LoaderContext } from '../loader/loaderState'
import { ValidationWarningModal, ValidationSubmitModal } from '../../components/misc'
import { NotificationContext } from '../notifications/notificationState'
import { DocumentsContext } from '../documents/documentsState'

export const ValidationContext = createContext()

const ValidationState = ({ children }) => {
  const initialState = {
    validations: {},
    myValidations: {},
    validationsFetched: false,
  }
  
  const { refreshDocument } = useContext(DocumentsContext)
  const { setNotification } = useContext(NotificationContext)
  const { setShowGlobalResponseLoader, setGlobalResponseLoaderText } = useContext(LoaderContext)
  const { user } = useContext(UserContext)

  const [state, dispatch] = useReducer(validationReducer, initialState)

  const [validationSubmitModal, setValidationSubmitModal] = useState({
    isOpen: false,
    validation: null,
    action: '',
  })

  const [validationWarningModal, setValidationWarningModal] = useState({
    isOpen: false,
    status: '',
    document: null,
    validation: null,
    onConfirm: () => { throw new Error('onConfirm function not provided') },
  })

  const [todoActiveSort, setTodoActiveSort] = useState({ 'date': 'desc' , activeItem: 'date'})

  useEffect(() => {
    const myValidations = {}
    if(!user) {
      dispatch({
        type: SET_MY_VALIDATIONS,
        payload: myValidations,
      })
      return
    }
    for(const key in state.validations) {
      const validation = state.validations[key]
      if((user?.validation_access === 'manager' && validation.validatorIds?.includes(`${user.id}`))) {
          myValidations[key] = {
            ...validation,
            id: key,
          }
      } else if(validation.metadata?.userId === `${user.id}`) {
        myValidations[key] = {
          ...validation,
          id: key,
        }
      }
    }
    dispatch({
      type: SET_MY_VALIDATIONS,
      payload: myValidations,
    })

  }, [state.validations, user])

  const fetchValidations = async () => {
    const res = await fetch_validations()
    if(res.error) {
      console.error('Error fetching validations', res.error)
      return res
    }
    dispatch({
      type: SET_VALIDATIONS,
      payload: res,
    })
  }

  const createValidation = async (validation) => {
    setShowGlobalResponseLoader(true)
    setGlobalResponseLoaderText('Envoi de la validation...')
    const res = await create_validation(validation)
    setShowGlobalResponseLoader(false)
    setGlobalResponseLoaderText('')
    if(res.error) {
      return res
    }
    const newValidation = res.data
    const validationsUpdated = {
      ...state.validations,
      [newValidation.id]: {
        ...newValidation,
        id: newValidation.id,
      }
    }
    dispatch({
      type: SET_VALIDATIONS,
      payload: validationsUpdated,
    })
  }

  const promptResolveValidation = async (validation, action) => {
    setValidationSubmitModal({
      isOpen: true,
      validation,
      action,
    })
  }

  const cancelResolveValidation = () => {
    setValidationSubmitModal({
      isOpen: false,
      validation: null,
      action: '',
    })
  }

  const resolveValidation = async (validation, action, comment) => {
    setShowGlobalResponseLoader(true)
    setGlobalResponseLoaderText('Soumission de la validation...')
    const res = await resolve_validation(validation.id, { action, comment })
    setShowGlobalResponseLoader(false)
    setGlobalResponseLoaderText('')
    if(res.error) {
      setNotification({
        msg: 'Erreur lors de la soumission de la validation',
        type: 'danger',
      })
      return res
    }
    const updatedValidation = res.data
    const validationsUpdated = {
      ...state.validations,
      [validation.id]: {
        ...updatedValidation,
        id: updatedValidation.id,
      }
    }
    dispatch({
      type: SET_VALIDATIONS,
      payload: validationsUpdated,
    })

    const document = await refreshDocument(validation.documentId)

    await add_document_event(validation.documentId, {
      type: `validation_${action}`,
      comment: comment || '',
      values: document?.values || {},
      content_changes: document?.content_changes || [],
    })
    cancelResolveValidation()
  }

  const showValidationWarning = ({document, action, validation, status, onConfirm}) => {
    setValidationWarningModal(modal => ({
      ...modal,
      isOpen: true,
      document: document,
      status,
      onConfirm,
      action,
      validation,
    }))
  }

  const closeValidationWarning = () => {
    setValidationWarningModal(modal => ({
      ...modal,
      isOpen: false,
    }))
  }

  return <ValidationContext.Provider value={{
    validations: state.validations,
    myValidations: state.myValidations,
    validationsFetched: state.validationsFetched,
    todoActiveSort,
    fetchValidations,
    createValidation,
    promptResolveValidation,
    resolveValidation,
    showValidationWarning,
    closeValidationWarning,
    setTodoActiveSort,
  }}>
    {children}
    {validationSubmitModal.isOpen &&
      <ValidationSubmitModal
        validation={validationSubmitModal.validation}
        action={validationSubmitModal.action}
        onCancel={cancelResolveValidation}
        onSubmit={resolveValidation} />
    }
    {validationWarningModal.isOpen && (
      <ValidationWarningModal
        onClose={closeValidationWarning}
        status={validationWarningModal.status}
        onConfirm={validationWarningModal.onConfirm}
        document={validationWarningModal.document}
        action={validationWarningModal.action}
        validation={validationWarningModal.validation}
      />
    )}
  </ValidationContext.Provider>
}

export default ValidationState
